import { WallsGame } from './WallsGame.js';
import { WallsRenderer, START_RING_RADIUS } from './WallsRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Edge } from '../../core/GridHitDetector.js';
import { Direction, edgesEqual } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, FinePoint, FineTraceStep } from '../../input/KeyboardController.js';

const OPPOSITE: Record<Direction, Direction> = { N: 'S', S: 'N', E: 'W', W: 'E' };

export class WallsInput extends BaseInput<WallsGame, WallsRenderer> implements KeyboardControllable {
    private hoveredEdge: Edge | null = null;
    private gateMode: boolean = false;

    constructor(
        game: WallsGame,
        renderer: WallsRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mouseleave', this.handleMouseLeave);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
        this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    private handleClick = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);

        // Gate mode: vortex gate activation/deactivation
        if (this.gateMode && isVortexGame(this.game)) {
            const cell = this.detector.detectCell(x, y);
            if (cell && this.game.toggleVortexGate(cell)) {
                this.onUpdate();
            }
            return;
        }

        // Clicking the start marker itself turns it, overriding the auto-chosen
        // direction. Checked before edges: the ring is drawn over the inner part of
        // the wall-click zone, so on that one cell the ring wins.
        const center = this.detector.isNearCellCenter(x, y, START_RING_RADIUS);
        if (center && this.game.rotateStartDirection(center)) {
            this.onUpdate();
            return;
        }

        // Normal mode: wall placement
        const edge = this.detector.detectEdge(x, y, 0.25);
        if (edge && this.game.isValidEdge(edge)) {
            this.game.cycleWall(edge);
            this.onUpdate();
        }
    };

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y, 0.25);

        if (edge && this.game.isValidEdge(edge)) {
            this.game.setWall(edge, null);
            this.onUpdate();
        }
    };

    private handleMouseMove = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y, 0.25);

        const edgeChanged = !this.nullableEdgesEqual(edge, this.hoveredEdge);
        if (edgeChanged) {
            this.hoveredEdge = edge;
            // Could trigger hover highlight here if desired
        }
    };

    private handleMouseLeave = (): void => {
        this.hoveredEdge = null;
    };

    private nullableEdgesEqual(a: Edge | null, b: Edge | null): boolean {
        if (a === null && b === null) return true;
        if (a === null || b === null) return false;
        return edgesEqual(a, b);
    }

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Walls are on edges, so the cursor rides the half-grid. Place/delete toggle
    // a wall on an edge; the trace lays walls Mirrors-style along a cell path.

    usesFineCursor(): boolean {
        return true;
    }

    /** Each hop lays an arrow pointing where the block goes — the hint echoes it. */
    wantsTraceHint(): boolean {
        return true;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const edge = this.fineToEdge(p);
        if (!edge || !this.game.isValidEdge(edge)) return false;
        this.game.cycleWall(edge);
        return true;
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;
        const edge = this.fineToEdge(p);
        if (!edge || this.game.getWallAt(edge) === null) return false;
        this.game.setWall(edge, null);
        return true;
    }

    /**
     * A cell-to-cell trace hop, leaving `cell` with the block travelling `fromDir`
     * and the player heading `moveDir`. The wall sits on the edge in front of the
     * block (the `fromDir` side of `cell`): a turn places the arrow that redirects
     * it to `moveDir`; a straight pass clears any wall there; a reversal is a no-op
     * (no single arrow bounces the block back on a valid edge orientation).
     */
    traceFine({ cell, fromDir, moveDir }: FineTraceStep): boolean {
        if (this.gateMode || fromDir === null) return false;
        const edge = this.game.getGrid().getEdgeFromPosition(cell, fromDir);
        if (!this.game.isValidEdge(edge)) return false; // rim edge: can't hold a wall

        if (fromDir === moveDir) {
            if (this.game.getWallAt(edge) === null) return false;
            this.game.setWall(edge, null);
            return true;
        }
        if (OPPOSITE[fromDir] === moveDir) return false; // reversal
        if (this.game.getWallAt(edge) === moveDir) return false;
        return this.game.setWall(edge, moveDir);
    }

    /** The internal edge a half-grid point sits on, or null for a center/intersection. */
    private fineToEdge(p: FinePoint): Edge | null {
        const rOdd = p.r % 2 === 1;
        const cOdd = p.c % 2 === 1;
        if (rOdd === cOdd) return null; // both odd (center) or both even (intersection)
        const grid = this.game.getGrid();
        if (rOdd) {
            // Vertical edge on the west side of the right-hand cell.
            return grid.getEdgeFromPosition({ row: (p.r - 1) / 2, col: p.c / 2 }, 'W');
        }
        // Horizontal edge on the north side of the lower cell.
        return grid.getEdgeFromPosition({ row: p.r / 2, col: (p.c - 1) / 2 }, 'N');
    }

    /** `X` goes to the first digit's cell center — where the block's loop must begin. */
    fineCursorOrigin(): FinePoint | null {
        const grid = this.game.getGrid();
        const d = grid.getDigitPosition(grid.minDigit);
        return d ? { r: 2 * d.row + 1, c: 2 * d.col + 1 } : null;
    }

}
